import _ from 'lodash';
import GCU from './GCU';
import { colorDecoder } from '../helpers/colorDecoder';
import Weather from './Weather';

const buildTrackerOutput = (trackerArray) => {
  const output = {
    description: null,
    popover: {
      content: ''
    },
    class: null
  };

  output.class = colorDecoder.tracker(trackerArray[1].mode);
  const total = trackerArray[0].value;
  trackerArray.forEach((status, index) => {
    if (index === 1) {
      output.description = `${_.round(status.value / total * 100, 1)}% ${_.capitalize(status.mode)} Mode`;
    }

    if (index === trackerArray.length - 1) {
      output.popover.content += `${_.capitalize(status.mode)} - ${_.round(status.value / total * 100, 1)}%`;
    } else if (index > 0) {
      output.popover.content += `${_.capitalize(status.mode)} - ${_.round(status.value / total * 100, 1)}% <br> `;
    }
  });

  return output;
};

const buildArmOutput = (armArray) => {
  const output = {
    description: null,
    popover: {
      content: ''
    },
    class: null
  };

  output.class = colorDecoder.armature(armArray[1].mode);
  const total = armArray[0].value;
  armArray.forEach((status, index) => {
    const errorIndex = status.mode.indexOf('Error');
    let mode = null;
    if (errorIndex === -1) {
      mode = _.capitalize(status.mode);
    } else {
      mode = `${_.capitalize(status.mode.substring(0, errorIndex))} ${_.capitalize(status.mode.substring(errorIndex))}`;
    }

    if (index === 1) {
      output.description = `${_.round(status.value / total * 100, 1)}% ${mode}`;
    }

    if (index === armArray.length - 1) {
      output.popover.content += `${mode} - ${_.round(status.value / total * 100, 1)}%`;
    } else if (index > 0) {
      output.popover.content += `${mode} - ${_.round(status.value / total * 100, 1)}% <br> `;
    }
  });

  return output;
};

class Site {
  constructor(site) {
    this.info = {
      name: site.name,
      lat: site.latitude,
      long: site.longitude,
      connection: {
        popover: {
          content: 'Loading'
        },
        class: 'text-secondary'
      }
    };
  }

  setGCUs(gcus, trackers, gcuStatus) {
    const siteGCUs = [];

    gcus.forEach((gcu) => {
      const gcuTrackers = trackers.filter(a => a.gcuCode === gcu.code);

      if (gcuTrackers.length > 0) {
        const siteGCU = new GCU(gcu);

        siteGCU.sumArmStatus(gcuTrackers);
        siteGCU.sumTrackerStatus(gcuTrackers);
        siteGCU.setGCUStatus(gcuStatus[gcu.code]);
        siteGCU.setArmColors();
        siteGCU.setTrackerColors();

        siteGCUs.push(siteGCU);
      }
    });

    this.gcus = siteGCUs;
  }

  setWeather(weather) {
    let weatherData = null;
    if (weather) {
      weatherData = Weather.formatWeather(weather);
    }

    this.weather = {
      icon: weatherData ? weatherData.icon : 'minus',
      description: weatherData ? weatherData.description : '-',
      class: 'bg-info',
      popover: {
        content: weatherData ? weatherData.popoverContent : 'PENDING'
      }
    };
  }

  setForecast(forecast) {
    let forecastData = null;
    if (forecast) {
      forecastData = Weather.formatForecast(forecast);
    }

    this.forecast = {
      class: forecastData ? forecastData.class : 'bg-warning',
      loading: !forecast,
      loadingIcon: 'minus',
      description: forecastData ? forecastData.description : '-',
      popover: {
        content: forecastData ? forecastData.popoverContent : 'PENDING'
      }
    };
  }

  setStatus() {
    const outputStatus = {};
    const statusCount = {
      online: 0,
      offline: 0,
      pending: 0
    };

    this.gcus.forEach((gcu) => {
      switch (gcu.status) {
        case 'ONLINE':
          statusCount.online++;
          break;
        case 'OFFLINE':
          statusCount.offline++;
          break;
        case 'PENDING':
          statusCount.pending++;
          break;
        default:
          break;
      }
    });

    if (statusCount.pending > 0) {
      outputStatus.popover = {
        content: 'Loading'
      };
      outputStatus.class = 'text-secondary';
    } else if (statusCount.online === 0) {
      outputStatus.popover = {
        content: 'Site Offline'
      };
      outputStatus.class = 'text-danger';
    } else if (statusCount.offline === 0) {
      outputStatus.popover = {
        content: 'Site Online'
      };
      outputStatus.class = 'text-success';
    } else {
      outputStatus.popover = {
        content: `${statusCount.offline}/${statusCount.online + statusCount.offline} GCUs Offline`
      };
      outputStatus.class = 'text-warning';
    }

    this.info.connection = outputStatus;
  }

  setComponentInfo(type) {
    let outputObj = {
      description: null,
      popover: {
        content: null
      },
      class: null
    };

    let component = null;
    if (type === 'tracker') {
      component = {
        total: 0,
        track: 0,
        stow: 0,
        idle: 0,
        sleep: 0,
        error: 0,
        other: 0,
        unknown: 0
      };
    } else if (type === 'armature') {
      component = {
        total: 0,
        healthy: 0,
        azimuthError: 0,
        elevationError: 0,
        otherError: 0,
        unknownError: 0
      };
    }

    let compOutput = null;
    if (this.gcus.length > 0) {
      this.gcus.forEach((gcu) => {
        Object.keys(component).forEach((key) => {
          component[key] += gcu[type][key];
        });
      });

      const compArray = Object.keys(component).map((a) => {
        return {
          mode: a,
          value: component[a]
        };
      }).sort((a, b) => {
        return b.value - a.value;
      });
      if (type === 'tracker') {
        compOutput = buildTrackerOutput(compArray);
      } else if (type === 'armature') {
        compOutput = buildArmOutput(compArray);
      }

    }

    switch (this.status) {
      case 'PENDING':
        outputObj.description = 'PENDING';
        outputObj.class = 'bg-warning';
        outputObj.popover.content = 'PENDING';
        break;
      default:
        outputObj = compOutput;
        break;
    }

    if (type === 'tracker') {
      this.tracker = outputObj;
    } else if (type === 'armature') {
      this.armature = outputObj;
    }
  }

}

export default Site;
